import {
  PHONE_VALIDATION_MESSAGE,
  hasValidPhoneNumberCharacters,
  isValidPhoneNumber,
  normalizePhoneNumber,
} from './phone'

export const EXPECTED_HEADERS = ['name', 'phone', 'email', 'address', 'street', 'ward', 'district', 'province', 'tags', 'notes']

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
const EMAIL_MAX_LENGTH = 254
const NAME_MAX_LENGTH = 100
const ADDRESS_MAX_LENGTH = 255
const ADDRESS_PART_MAX_LENGTH = 120
const NOTES_MAX_LENGTH = 1000

function validateEmailStrict(email) {
  if (email.length > EMAIL_MAX_LENGTH) {
    return `Email is too long (max ${EMAIL_MAX_LENGTH} characters)`
  }
  if (!EMAIL_RE.test(email)) {
    return 'Invalid email format'
  }

  const [local = '', domain = ''] = email.split('@')
  if (!local || !domain) return 'Invalid email format'
  if (local.startsWith('.') || local.endsWith('.')) return 'Invalid email format'
  if (email.includes('..')) return 'Email cannot contain consecutive dots'
  if (domain.startsWith('-') || domain.endsWith('-') || domain.startsWith('.') || domain.endsWith('.')) {
    return 'Invalid email domain'
  }
  if (domain.includes('..')) return 'Invalid email domain'

  return null
}

/**
 * Validate and normalize one CSV data row.
 *
 * @param {object} row - key/value from parseCsv (keys already lowercased)
 * @param {Array<{phone:string, email:string}>} existingContactsInBatch
 *   Normalized contacts already processed earlier in the same file.
 *   Index 0 = file row 2, index 1 = file row 3, etc.
 * @param {{ stage?: 'customer' | 'follower' }} options
 * @returns {{ valid, hasWarning, errors, warnings, normalized }}
 */
export function validateRow(row, existingContactsInBatch = [], options = {}) {
  const fieldErrors = []
  const batchWarnings = []
  const stage = options.stage === 'customer' ? 'customer' : 'follower'
  const isCustomerStage = stage === 'customer'

  const name    = (row.name    || '').trim()
  const rawPhone = (row.phone || '').trim()
  const rawEmail = (row.email   || '').trim().toLowerCase()
  const address  = (row.address || '').trim()
  const street   = (row.street   || '').trim()
  const ward     = (row.ward     || '').trim()
  const district = (row.district || '').trim()
  const province = (row.province || '').trim()
  const notes    = (row.notes   || '').trim()
  const tags     = (row.tags    || '').split(/[;,]/).map(t => t.trim()).filter(Boolean)

  if (!name) fieldErrors.push('Name is required')
  else if (name.length > NAME_MAX_LENGTH) fieldErrors.push(`Name is too long (max ${NAME_MAX_LENGTH} characters)`)

  let phone = ''
  if (isCustomerStage && !rawPhone) {
    fieldErrors.push('Phone is required for customer import')
  } else if (rawPhone) {
    const normalizedPhone = normalizePhoneNumber(rawPhone)
    if (!hasValidPhoneNumberCharacters(rawPhone)) {
      fieldErrors.push('Phone contains invalid characters')
    } else if (!isValidPhoneNumber(normalizedPhone)) {
      fieldErrors.push(PHONE_VALIDATION_MESSAGE)
    } else {
      phone = normalizedPhone
    }
  }

  let email = ''
  if (rawEmail) {
    const emailError = validateEmailStrict(rawEmail)
    if (emailError) {
      fieldErrors.push(emailError)
    } else {
      email = rawEmail
    }
  }

  if (address.length > ADDRESS_MAX_LENGTH) {
    fieldErrors.push(`Address is too long (max ${ADDRESS_MAX_LENGTH} characters)`)
  }

  if (street.length > ADDRESS_PART_MAX_LENGTH) {
    fieldErrors.push(`Street is too long (max ${ADDRESS_PART_MAX_LENGTH} characters)`)
  }
  if (ward.length > ADDRESS_PART_MAX_LENGTH) {
    fieldErrors.push(`Ward is too long (max ${ADDRESS_PART_MAX_LENGTH} characters)`)
  }
  if (district.length > ADDRESS_PART_MAX_LENGTH) {
    fieldErrors.push(`District is too long (max ${ADDRESS_PART_MAX_LENGTH} characters)`)
  }
  if (province.length > ADDRESS_PART_MAX_LENGTH) {
    fieldErrors.push(`Province is too long (max ${ADDRESS_PART_MAX_LENGTH} characters)`)
  }

  if (notes.length > NOTES_MAX_LENGTH) {
    fieldErrors.push(`Notes is too long (max ${NOTES_MAX_LENGTH} characters)`)
  }

  // Duplicate check within the batch
  existingContactsInBatch.forEach((prev, i) => {
    const prevRowNum = i + 2 // header = row 1, first data = row 2
    if (phone && prev.phone && prev.phone === phone) {
      batchWarnings.push(`Duplicate phone in file (row ${prevRowNum})`)
    }
    if (email && prev.email && prev.email === email) {
      batchWarnings.push(`Duplicate email in file (row ${prevRowNum})`)
    }
  })

  return {
    valid:      fieldErrors.length === 0,
    hasWarning: batchWarnings.length > 0,
    errors:     fieldErrors,
    warnings:   batchWarnings,
    normalized: { name, phone, email, address, street, ward, district, province, tags, notes },
  }
}
